<template>
    <div>
        <div v-if="loading" class="ta-loading">
            <div class="spinner-border text-primary" role="status">
                <span class="sr-only">Chargement...</span>
            </div>
        </div>

        <template v-else-if="applications && applications.data && applications.data.length > 0">
            <div class="ta-table-wrap">
                <table class="ta-table">
                    <thead>
                        <tr>
                            <th class="ta-th">{{ $t('forms.applications.reference') }}</th>
                            <th class="ta-th">{{ $t('forms.applications.name') }}</th>
                            <th class="ta-th">{{ $t('forms.applications.source') }}</th>
                            <th class="ta-th ta-th--center">{{ $t('forms.applications.active.label') }}</th>
                            <th class="ta-th ta-th--center">{{ $t('forms.applications.lang.label') }}</th>
                            <th class="ta-th">{{ $t('forms.applications.client_url') }}</th>
                            <th class="ta-th">{{ $t('forms.applications.created_by') }}</th>
                            <th class="ta-th">{{ $t('forms.applications.created_at') }}</th>
                            <th class="ta-th ta-th--actions">{{ $t('forms.applications.actions') }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="row in applications.data"
                            :key="row.id"
                            class="ta-row"
                            :class="{ 'ta-row--deleted': row.deleted_at }"
                        >
                            <td class="ta-td">
                                <span class="ta-ref">#{{ row.id }}</span>
                            </td>
                            <td class="ta-td">
                                <span class="ta-name">{{ row.name }}</span>
                            </td>
                            <td class="ta-td">
                                <span class="ta-source-badge">{{ row.source }}</span>
                            </td>
                            <td class="ta-td ta-td--center">
                                <span
                                    class="ta-status-badge"
                                    :class="row.active ? 'ta-status--active' : 'ta-status--inactive'"
                                >
                                    {{ $t(`forms.applications.active.${row.active}`) }}
                                </span>
                            </td>
                            <td class="ta-td ta-td--center">
                                <span class="ta-lang-badge">{{ $t(`forms.applications.lang.${row.lang}`) }}</span>
                            </td>
                            <td class="ta-td">
                                <span class="ta-url">{{ row.client_url }}</span>
                            </td>
                            <td class="ta-td">
                                <span class="ta-creator">{{ row.created_by ? row.created_by.name : '—' }}</span>
                            </td>
                            <td class="ta-td">
                                <span class="ta-date">{{ moment(row.created_at).format('L') }}</span>
                            </td>
                            <td class="ta-td ta-td--actions" v-if="!row.deleted_at">
                                <div class="ta-actions">
                                    <router-link
                                        :to="`/forms/${form.id}/app/${row.id}/edit`"
                                        class="ta-btn ta-btn--edit"
                                        :title="$t('common.edit') || 'Modifier'"
                                    >
                                        <i class="fal fa-pen" style="color: #fff !important;"></i>
                                    </router-link>
                                    <a
                                        href="#"
                                        class="ta-btn ta-btn--download"
                                        :title="'Télécharger'"
                                        @click.prevent="downloadFile(row.id)"
                                    >
                                        <i class="fal fa-download" style="color: #fff !important;"></i>
                                    </a>
                                    <button
                                        class="ta-btn ta-btn--delete"
                                        :title="'Supprimer'"
                                        @click.prevent="deleteApp(row)"
                                    >
                                        <i class="fal fa-trash" style="color: #fff !important;"></i>
                                    </button>
                                </div>
                            </td>
                            <td v-else class="ta-td ta-td--actions"></td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Pagination -->
            <div v-if="applications.last_page > 1" class="ta-pagination">
                <button
                    class="ta-page-btn"
                    :disabled="applications.current_page <= 1"
                    @click="onPageChange(applications.current_page - 1)"
                >
                    <i class="fal fa-chevron-left"></i>
                </button>
                <span class="ta-page-info">
                    {{ applications.current_page }} / {{ applications.last_page }}
                </span>
                <button
                    class="ta-page-btn"
                    :disabled="applications.current_page >= applications.last_page"
                    @click="onPageChange(applications.current_page + 1)"
                >
                    <i class="fal fa-chevron-right"></i>
                </button>
            </div>
        </template>

        <div v-else class="ta-empty">
            <i class="fal fa-code-branch"></i>
            <p>{{ $t('forms.applications.no_deploy') }}</p>
        </div>

        <DeleteApplication :application="appToDelete" ref="deleteApp" />
    </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import DeleteApplication from './DeleteApplication.vue'

export default {
    name: 'TabApplication',
    props: ['form'],
    components: {
        DeleteApplication
    },
    data: function () {
        return {
            appToDelete: {},
            loading: false
        }
    },
    created() {
        this.loadAsyncData()
    },
    computed: {
        ...mapState('application', {
            applications: (state) => state.applications,
            config: (state) => state.applicationsConfig
        })
    },
    methods: {
        ...mapActions('application', [
            'getApplications',
            'download',
            'deleteApplication'
        ]),
        loadAsyncData() {
            this.loading = true
            this.getData()
        },
        getData() {
            this.getApplications(this.form.id)
                .then(() => {
                    this.loading = false
                })
                .catch((error) => {
                    this.loading = false
                    this.$store.dispatch('dispatchError', error)
                })
        },
        onSort(field, order) {
            this.config.sort = field
            this.config.sortDirection = order
            this.loadAsyncData()
        },
        onFilterChange(filter) {
            var filters = {}
            Object.keys(filter).forEach((element) => {
                filters[element] = filter[element]
            })
            this.config.filters = filters
            this.loadAsyncData()
        },
        onPageChange(page) {
            this.config.page = page
            this.loadAsyncData()
        },
        downloadFile(id) {
            this.download({ formId: this.form.id, id: id }).then((fileText) => {
                this.toFile(fileText, '.env', 'text/plain')
            })
        },
        toFile(text, name, type) {
            var file = new Blob([text], { type: type })
            var a = document.createElement('a')
            a.href = URL.createObjectURL(file)
            a.download = name
            a.click()
        },
        deleteApp(app) {
            this.appToDelete = app
            this.$refs.deleteApp.trigger()
        }
    }
}
</script>

<style scoped>
.ta-loading {
    display: flex;
    justify-content: center;
    padding: 48px 0;
}

/* Table */
.ta-table-wrap {
    overflow-x: auto;
    border-radius: 12px;
    border: 1px solid #e5e7eb;
}

.ta-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.84rem;
}

.ta-th {
    padding: 12px 14px;
    text-align: left;
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #64748b;
    background: #f8fafc;
    border-bottom: 2px solid #e5e7eb;
    white-space: nowrap;
}

.ta-th--center {
    text-align: center;
}

.ta-th--actions {
    text-align: center;
    width: 130px;
}

.ta-row {
    transition: background 0.15s;
}

.ta-row:hover {
    background: #f8fafc;
}

.ta-row--deleted {
    opacity: 0.4;
}

.ta-td {
    padding: 12px 14px;
    border-bottom: 1px solid #f1f5f9;
    color: #334155;
    vertical-align: middle;
}

.ta-td--center {
    text-align: center;
}

.ta-td--actions {
    text-align: center;
}

/* Elements */
.ta-ref {
    font-size: 0.78rem;
    font-weight: 600;
    color: #94a3b8;
}

.ta-name {
    font-weight: 600;
    color: #0f172a;
}

.ta-source-badge {
    display: inline-flex;
    padding: 3px 10px;
    background: #f1f5f9;
    color: #475569;
    border-radius: 6px;
    font-size: 0.75rem;
    font-weight: 600;
}

.ta-status-badge {
    display: inline-flex;
    padding: 3px 10px;
    border-radius: 20px;
    font-size: 0.72rem;
    font-weight: 600;
}

.ta-status--active {
    background: #dcfce7;
    color: #16a34a;
}

.ta-status--inactive {
    background: #fef2f2;
    color: #dc2626;
}

.ta-lang-badge {
    display: inline-flex;
    padding: 3px 10px;
    background: #eff6ff;
    color: #3b82f6;
    border-radius: 6px;
    font-size: 0.72rem;
    font-weight: 600;
    text-transform: uppercase;
}

.ta-url {
    font-size: 0.78rem;
    color: #64748b;
    max-width: 180px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: block;
}

.ta-creator {
    font-size: 0.82rem;
    color: #334155;
}

.ta-date {
    font-size: 0.78rem;
    color: #94a3b8;
}

/* Actions */
.ta-actions {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
}

.ta-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    border: none;
    cursor: pointer;
    text-decoration: none;
    transition: all 0.2s ease;
}

.ta-btn i {
    font-size: 0.78rem;
}

.ta-btn--edit {
    background: #3b82f6;
}

.ta-btn--edit:hover {
    background: #2563eb;
    transform: translateY(-1px);
    box-shadow: 0 3px 8px rgba(59, 130, 246, 0.3);
}

.ta-btn--download {
    background: #10b981;
}

.ta-btn--download:hover {
    background: #059669;
    transform: translateY(-1px);
    box-shadow: 0 3px 8px rgba(16, 185, 129, 0.3);
}

.ta-btn--delete {
    background: #ef4444;
}

.ta-btn--delete:hover {
    background: #dc2626;
    transform: translateY(-1px);
    box-shadow: 0 3px 8px rgba(239, 68, 68, 0.3);
}

/* Pagination */
.ta-pagination {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 16px 0;
}

.ta-page-btn {
    width: 34px;
    height: 34px;
    border-radius: 8px;
    border: 1px solid #e5e7eb;
    background: #fff;
    color: #64748b;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;
}

.ta-page-btn:hover:not(:disabled) {
    border-color: #3b82f6;
    color: #3b82f6;
    background: #eff6ff;
}

.ta-page-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
}

.ta-page-info {
    font-size: 0.82rem;
    font-weight: 600;
    color: #64748b;
}

/* Empty */
.ta-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 48px 0;
    color: #94a3b8;
}

.ta-empty i {
    font-size: 2.5rem;
    margin-bottom: 12px;
}

.ta-empty p {
    font-size: 0.88rem;
}
</style>
